import { Injectable } from '@angular/core';
import { supabase } from '../../../lib/supabase';
import { environment } from '../../../environments/environment';
import { FinderTheme } from '../models/finder/finder.model';
import { VR_THEME } from '../constants/vr-theme';

export interface Organization {
  id: string;
  name: string;
  logoPath?: string | null;
  theme?: FinderTheme | null;
  impressumUrl?: string | null;
  privacyPolicyUrl?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface OrganizationBranding {
  organizationName: string;
  logoPath: string | null;
  theme?: FinderTheme | null;
  impressumUrl?: string | null;
  privacyPolicyUrl?: string | null;
  updatedAt?: string;
}

export interface OrganizationLegalLinks {
  impressumUrl: string | null;
  privacyPolicyUrl: string | null;
}

export interface OrganizationMember {
  userId: string;
  role: 'org_admin' | 'member';
  email: string;
  fullName: string | null;
  createdAt?: string;
}

export interface OrganizationInvite {
  id: string;
  email: string;
  role: 'org_admin' | 'member';
  expiresAt: string;
  createdAt?: string;
}

export interface InvitePreview {
  inviteId: string;
  email: string;
  organizationId: string;
  organizationName: string;
  role: 'org_admin' | 'member';
  expiresAt: string;
  acceptedAt: string | null;
  isValid: boolean;
}

export interface InviteResponse {
  success: boolean;
  error?: string;
  existingUser?: boolean;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  private static readonly ORG_SELECT =
    'id, name, logo_path, theme, impressum_url, privacy_policy_url, created_at, updated_at';

  private readonly logosBucket = environment.supabaseOrganizationLogosBucket;
  private readonly maxLogoBytes = 2 * 1024 * 1024;
  private readonly allowedLogoTypes = new Set([
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/svg+xml',
  ]);

  private mapOrganization(row: {
    id: string;
    name: string;
    logo_path?: string | null;
    theme?: unknown;
    impressum_url?: string | null;
    privacy_policy_url?: string | null;
    created_at?: string;
    updated_at?: string;
  }): Organization {
    return {
      id: row.id,
      name: row.name,
      logoPath: (row.logo_path as string | null | undefined) ?? null,
      theme: this.parseTheme(row.theme),
      impressumUrl: (row.impressum_url as string | null | undefined) ?? null,
      privacyPolicyUrl: (row.privacy_policy_url as string | null | undefined) ?? null,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    };
  }

  private parseTheme(value: unknown): FinderTheme | null {
    if (!value || typeof value !== 'object') {
      return null;
    }
    return value as FinderTheme;
  }

  resolveEffectiveTheme(
    orgTheme?: FinderTheme | null,
    finderTheme?: FinderTheme | null
  ): FinderTheme {
    return orgTheme ?? finderTheme ?? VR_THEME;
  }

  resolveLogoPublicUrl(
    logoPath: string | null | undefined,
    updatedAt?: string
  ): string | null {
    if (!logoPath) {
      return null;
    }
    const { data } = supabase.storage.from(this.logosBucket).getPublicUrl(logoPath);
    if (!data.publicUrl) {
      return null;
    }
    if (!updatedAt) {
      return data.publicUrl;
    }
    const version = new Date(updatedAt).getTime();
    return Number.isFinite(version) ? `${data.publicUrl}?v=${version}` : data.publicUrl;
  }

  async getOrganization(organizationId: string): Promise<Organization | null> {
    const { data, error } = await supabase
      .from('organizations')
      .select(OrganizationService.ORG_SELECT)
      .eq('id', organizationId)
      .maybeSingle();

    if (error) throw error;
    return data ? this.mapOrganization(data) : null;
  }

  async getPublicBranding(organizationId: string): Promise<OrganizationBranding | null> {
    const { data, error } = await supabase.rpc('get_public_organization_branding', {
      p_organization_id: organizationId,
    });

    if (error) throw error;
    const row = data?.[0];
    if (!row) {
      return null;
    }

    return {
      organizationName: row.organization_name as string,
      logoPath: (row.logo_path as string | null) ?? null,
      theme: this.parseTheme(row.theme),
      impressumUrl: (row.impressum_url as string | null) ?? null,
      privacyPolicyUrl: (row.privacy_policy_url as string | null) ?? null,
      updatedAt: (row.updated_at as string | undefined) ?? undefined,
    };
  }

  async updateOrganizationTheme(
    organizationId: string,
    theme: FinderTheme
  ): Promise<Organization> {
    const { data, error } = await supabase
      .from('organizations')
      .update({
        theme,
        updated_at: new Date().toISOString(),
      })
      .eq('id', organizationId)
      .select(OrganizationService.ORG_SELECT)
      .single();

    if (error) throw error;
    return this.mapOrganization(data);
  }

  async updateOrganizationLegalLinks(
    organizationId: string,
    legalLinks: OrganizationLegalLinks
  ): Promise<Organization> {
    const { data, error } = await supabase
      .from('organizations')
      .update({
        impressum_url: legalLinks.impressumUrl?.trim() || null,
        privacy_policy_url: legalLinks.privacyPolicyUrl?.trim() || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', organizationId)
      .select(OrganizationService.ORG_SELECT)
      .single();

    if (error) throw error;
    return this.mapOrganization(data);
  }

  async uploadOrganizationLogo(organizationId: string, file: File): Promise<Organization> {
    if (!this.allowedLogoTypes.has(file.type)) {
      throw new Error('Bitte PNG, JPEG, WebP oder SVG hochladen.');
    }
    if (file.size > this.maxLogoBytes) {
      throw new Error('Logo darf maximal 2 MB groß sein.');
    }

    const org = await this.getOrganization(organizationId);
    if (!org) {
      throw new Error('Organisation nicht gefunden.');
    }

    const ext = this.logoExtension(file);
    const path = `${organizationId}/logo${ext}`;

    if (org.logoPath && org.logoPath !== path) {
      await supabase.storage.from(this.logosBucket).remove([org.logoPath]);
    }

    const { error: uploadError } = await supabase.storage
      .from(this.logosBucket)
      .upload(path, file, {
        cacheControl: '3600',
        contentType: file.type,
        upsert: true,
      });

    if (uploadError) {
      throw new Error(uploadError.message || 'Logo konnte nicht hochgeladen werden.');
    }

    const { data, error } = await supabase
      .from('organizations')
      .update({
        logo_path: path,
        updated_at: new Date().toISOString(),
      })
      .eq('id', organizationId)
      .select(OrganizationService.ORG_SELECT)
      .single();

    if (error) throw error;
    return this.mapOrganization(data);
  }

  async removeOrganizationLogo(organizationId: string): Promise<Organization> {
    const org = await this.getOrganization(organizationId);
    if (!org) {
      throw new Error('Organisation nicht gefunden.');
    }

    if (org.logoPath) {
      await supabase.storage.from(this.logosBucket).remove([org.logoPath]);
    }

    const { data, error } = await supabase
      .from('organizations')
      .update({
        logo_path: null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', organizationId)
      .select(OrganizationService.ORG_SELECT)
      .single();

    if (error) throw error;
    return this.mapOrganization(data);
  }

  private logoExtension(file: File): string {
    switch (file.type) {
      case 'image/png':
        return '.png';
      case 'image/jpeg':
        return '.jpg';
      case 'image/webp':
        return '.webp';
      case 'image/svg+xml':
        return '.svg';
      default:
        return '';
    }
  }

  /** Organizations the signed-in user belongs to (RLS). */
  async getMyOrganizations(): Promise<Organization[]> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return [];

    const { data: memberships, error: mErr } = await supabase
      .from('organization_members')
      .select('organization_id')
      .eq('user_id', user.id);

    if (mErr) throw mErr;
    const ids = (memberships || []).map((r) => r.organization_id as string).filter(Boolean);
    if (ids.length === 0) return [];

    const { data: orgs, error: oErr } = await supabase
      .from('organizations')
      .select(OrganizationService.ORG_SELECT)
      .in('id', ids)
      .order('name', { ascending: true });

    if (oErr) throw oErr;
    return (orgs || []).map((o) => this.mapOrganization(o));
  }

  /**
   * First organization membership for the current user, or null.
   * Used when creating a finder without an explicit org id.
   */
  async getPrimaryOrganizationId(): Promise<string | null> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('organization_members')
      .select('organization_id')
      .eq('user_id', user.id)
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return data?.organization_id ?? null;
  }

  async isPlatformAdmin(): Promise<boolean> {
    const { data, error } = await supabase.rpc('is_platform_admin');
    if (error) throw error;
    return !!data;
  }

  async listAllOrganizations(): Promise<Organization[]> {
    const { data, error } = await supabase
      .from('organizations')
      .select(OrganizationService.ORG_SELECT)
      .order('name', { ascending: true });

    if (error) throw error;
    return (data || []).map((o) => this.mapOrganization(o));
  }

  async createOrganization(name: string): Promise<Organization> {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('organizations')
      .insert({
        name: name.trim(),
        created_by: user?.id ?? null,
      })
      .select(OrganizationService.ORG_SELECT)
      .single();

    if (error) throw error;
    return this.mapOrganization(data);
  }

  async deleteOrganization(organizationId: string): Promise<void> {
    const org = await this.getOrganization(organizationId);
    if (org?.logoPath) {
      await supabase.storage.from(this.logosBucket).remove([org.logoPath]);
    }

    const { error } = await supabase.from('organizations').delete().eq('id', organizationId);
    if (error) throw error;
  }

  async getOrganizationMembers(organizationId: string): Promise<OrganizationMember[]> {
    const { data: members, error: mErr } = await supabase
      .from('organization_members')
      .select('user_id, role, created_at')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: true });

    if (mErr) throw mErr;
    if (!members?.length) return [];

    const userIds = members.map((m) => m.user_id as string);
    const { data: profiles, error: pErr } = await supabase
      .from('user_profiles')
      .select('id, email, full_name')
      .in('id', userIds);

    if (pErr) throw pErr;

    const profileById = new Map(
      (profiles || []).map((p) => [
        p.id as string,
        { email: (p.email as string) ?? '', fullName: (p.full_name as string | null) ?? null },
      ])
    );

    return members.map((m) => {
      const profile = profileById.get(m.user_id as string);
      return {
        userId: m.user_id as string,
        role: m.role as 'org_admin' | 'member',
        email: profile?.email ?? '',
        fullName: profile?.fullName ?? null,
        createdAt: m.created_at as string | undefined,
      };
    });
  }

  async getPendingInvites(organizationId: string): Promise<OrganizationInvite[]> {
    const { data, error } = await supabase
      .from('organization_invites')
      .select('id, email, role, expires_at, created_at')
      .eq('organization_id', organizationId)
      .is('accepted_at', null)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map((i) => ({
      id: i.id as string,
      email: i.email as string,
      role: i.role as 'org_admin' | 'member',
      expiresAt: i.expires_at as string,
      createdAt: i.created_at as string | undefined,
    }));
  }

  async removeMember(organizationId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('organization_members')
      .delete()
      .eq('organization_id', organizationId)
      .eq('user_id', userId);

    if (error) throw error;
  }

  async revokeInvite(inviteId: string): Promise<void> {
    const { error } = await supabase.from('organization_invites').delete().eq('id', inviteId);
    if (error) throw error;
  }

  async sendInvite(
    organizationId: string,
    email: string,
    role: 'org_admin' | 'member' = 'member'
  ): Promise<InviteResponse> {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      return { success: false, error: 'Nicht angemeldet.' };
    }

    const response = await fetch(
      `${environment.supabaseUrl}/functions/v1/send-organization-invite`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          apikey: environment.supabaseAnonKey,
        },
        body: JSON.stringify({ organizationId, email, role }),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      return { success: false, error: result.error ?? 'Einladung konnte nicht gesendet werden.' };
    }

    return {
      success: true,
      existingUser: result.existingUser,
      message: result.message,
    };
  }

  async getInviteByToken(token: string): Promise<InvitePreview | null> {
    const { data, error } = await supabase.rpc('get_invite_by_token', { p_token: token });

    if (error) throw error;
    const row = data?.[0];
    if (!row) return null;

    return {
      inviteId: row.invite_id as string,
      email: row.email as string,
      organizationId: row.organization_id as string,
      organizationName: row.organization_name as string,
      role: row.role as 'org_admin' | 'member',
      expiresAt: row.expires_at as string,
      acceptedAt: (row.accepted_at as string | null) ?? null,
      isValid: !!row.is_valid,
    };
  }

  async completeInvite(token: string): Promise<void> {
    const { error } = await supabase.rpc('complete_organization_invite', { p_token: token });
    if (error) throw error;
  }

  async completeInviteWithRegistration(
    token: string,
    fullName: string,
    password: string
  ): Promise<{ success: boolean; error?: string; email?: string }> {
    const response = await fetch(
      `${environment.supabaseUrl}/functions/v1/complete-organization-invite`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: environment.supabaseAnonKey,
        },
        body: JSON.stringify({ token, fullName, password }),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      return { success: false, error: result.error ?? 'Registrierung fehlgeschlagen.' };
    }

    return { success: true, email: result.email as string };
  }
}
